/**
 * HammerSystem: manages the Hammer of Threia wall-breaking mechanic.
 *
 * The player can break a wall by holding an arrow key toward the wall
 * and pressing "B" rapidly (5 presses within 500ms intervals). Each
 * press triggers a hit sound + wall vibration + incremental glow.
 * After the 5th press the wall shatters with a celestial flash.
 *
 * Broken walls show a debris texture and regenerate after 45 seconds.
 * Each wall break costs 1 charge. Charges can come from floor pickups
 * (3) and/or the shop item (10). They stack additively.
 */

import Phaser from 'phaser';
import { Events, CellType, Direction, TEX_WALL_DEBRIS, TEX_GLOW } from '../utils/constants.js';
import {
  TILE_SIZE,
  HAMMER_BREAK_PRESSES,
  HAMMER_PRESS_INTERVAL,
  HAMMER_WALL_REGEN_TIME,
  HAMMER_GLOW_RADIUS,
  HAMMER_GLOW_FLASH_DURATION,
  HAMMER_VIBRATE_INTENSITY,
  AUDIO_HAMMER_HIT,
  AUDIO_HAMMER_BREAK,
  AUDIO_WALL_REGEN,
  COLOR_HAMMER_BLUE,
  COLOR_HAMMER_GOLD,
} from '../config/game-config.js';
import { getSfxVol } from '../utils/audio-config.js';
import MazeGenerator from './MazeGenerator.js';

/**
 * Hammer states.
 * @enum {string}
 */
const HammerState = Object.freeze({
  IDLE: 'idle',
  STRIKING: 'striking',
  BREAKING: 'breaking',
});

export default class HammerSystem {
  /**
   * @param {Phaser.Scene} scene - The GameScene.
   * @param {Phaser.Events.EventEmitter} eventBus - Shared event bus.
   * @param {import('../entities/Player.js').default} player
   */
  constructor(scene, eventBus, player) {
    /** @type {Phaser.Scene} */
    this.scene = scene;

    /** @type {Phaser.Events.EventEmitter} */
    this.eventBus = eventBus;

    /** @type {import('../entities/Player.js').default} */
    this.player = player;

    /** @type {HammerState} */
    this._state = HammerState.IDLE;

    /** @type {number} Total hammer charges (pickup + shop stacked). */
    this._charges = 0;

    /** @type {number} B-press count in current striking session. */
    this._pressCount = 0;

    /** @type {number} Timestamp of the last B press. */
    this._lastPressTime = 0;

    /** @type {Phaser.Time.TimerEvent|null} Cadence timeout. */
    this._cadenceTimer = null;

    /**
     * Currently targeted wall cell (row, col) during a striking session.
     * @type {{ row: number, col: number }|null}
     */
    this._targetWall = null;

    /**
     * Track broken walls for regeneration.
     * Map key: "row,col", value: { timer, debrisSprite, originalTexKey }
     * @type {Map<string, object>}
     */
    this._brokenWalls = new Map();

    /**
     * Graphics used for glow effects during striking.
     * @type {Phaser.GameObjects.Graphics|null}
     */
    this._strikeGlow = null;

    /** @type {boolean} */
    this._destroyed = false;
  }

  // ---------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------

  /**
   * Set the total hammer charges (e.g., from save restore or pickup).
   * @param {number} charges
   */
  setCharges(charges) {
    this._charges = charges;
    this.eventBus.emit(Events.HAMMER_CHARGES_CHANGED, this._charges);
  }

  /**
   * Add charges (e.g., from a floor pickup being collected).
   * @param {number} amount
   */
  addCharges(amount) {
    this._charges += amount;
    this.eventBus.emit(Events.HAMMER_CHARGES_CHANGED, this._charges);
  }

  /**
   * Get current charge count.
   * @returns {number}
   */
  getCharges() {
    return this._charges;
  }

  /**
   * Handle a B key press. Called from GameScene input handler.
   */
  onBPress() {
    if (this._destroyed) return;
    if (this._charges <= 0) return;
    if (this._state === HammerState.BREAKING) return;

    // Player must not be moving
    if (this.player.isMoving) return;

    // Determine which direction the player is facing (held cursor key)
    const dir = this._getHeldDirection();
    if (!dir) return;

    // Find the wall cell in that direction
    const pos = this.player.getGridPosition();
    const delta = HammerSystem._dirDelta(dir);
    const wallCol = pos.col + delta.dx;
    const wallRow = pos.row + delta.dy;

    // Must be within grid and be a wall
    if (!this._isWithinGrid(wallCol, wallRow)) return;
    if (this.scene.mazeGrid[wallRow][wallCol] !== CellType.WALL) return;

    // Don't allow breaking an already-broken wall
    const key = `${wallRow},${wallCol}`;
    if (this._brokenWalls.has(key)) return;

    const now = Date.now();

    // If we're already striking, check cadence and same wall target
    if (this._state === HammerState.STRIKING) {
      // Must be targeting the same wall
      if (!this._targetWall || this._targetWall.row !== wallRow || this._targetWall.col !== wallCol) {
        // Switched target — reset
        this._resetStrike();
      }

      // Check cadence
      if (now - this._lastPressTime > HAMMER_PRESS_INTERVAL) {
        // Too slow — reset
        this._resetStrike();
      }
    }

    // Start or continue striking
    this._state = HammerState.STRIKING;
    this._targetWall = { row: wallRow, col: wallCol };
    this._pressCount++;
    this._lastPressTime = now;

    // Reset cadence timer
    if (this._cadenceTimer) {
      this._cadenceTimer.remove(false);
      this._cadenceTimer = null;
    }

    // Play hit sound
    try {
      this.scene.sound.play(AUDIO_HAMMER_HIT, { volume: getSfxVol(0.5) });
    } catch (_) { /* audio fallback */ }

    // Striking is an active action — reset idle rest timer and stop resting
    this.scene._lastMoveTime = Date.now();
    if (this.scene._isResting) {
      this.scene._stopResting();
    }

    // Emit hit event for HUD feedback
    this.eventBus.emit(Events.HAMMER_HIT, this._pressCount, HAMMER_BREAK_PRESSES);

    // Visual feedback: wall vibration + incremental glow
    this._vibrateWall(wallCol, wallRow);
    this._drawStrikeGlow(wallCol, wallRow, this._pressCount / HAMMER_BREAK_PRESSES);

    // Check if we've reached the break threshold
    if (this._pressCount >= HAMMER_BREAK_PRESSES) {
      // Boundary walls (outermost ring of the maze) cannot be broken
      if (this._isBoundaryWall(wallCol, wallRow)) {
        this._resetStrike();
        return;
      }
      this._breakWall(wallCol, wallRow);
      return;
    }

    // Set cadence timeout — if player doesn't press again in time, reset
    this._cadenceTimer = this.scene.time.delayedCall(HAMMER_PRESS_INTERVAL + 100, () => {
      this._resetStrike();
    });
  }

  /**
   * Get the list of currently broken wall positions for saving.
   * @returns {Array<{ row: number, col: number, remaining: number }>}
   */
  getBrokenWallsForSave() {
    const result = [];
    for (const [key, data] of this._brokenWalls.entries()) {
      const [row, col] = key.split(',').map(Number);
      // Calculate remaining time
      const elapsed = Date.now() - data.brokenAt;
      const remaining = Math.max(0, HAMMER_WALL_REGEN_TIME - elapsed);
      result.push({ row, col, remaining });
    }
    return result;
  }

  /**
   * Restore broken walls from save data.
   * @param {Array<{ row: number, col: number, remaining: number }>} brokenWalls
   */
  restoreBrokenWalls(brokenWalls) {
    if (!brokenWalls || !Array.isArray(brokenWalls)) return;
    for (const bw of brokenWalls) {
      if (bw.remaining <= 0) continue;
      // Set the maze cell to PATH
      this.scene.mazeGrid[bw.row][bw.col] = CellType.PATH;
      // Remove the wall sprite at this position
      this._removeWallSprite(bw.col, bw.row);
      // Place debris
      const debrisSprite = this._placeDebris(bw.col, bw.row);
      // Schedule regeneration with remaining time
      const timer = this.scene.time.delayedCall(bw.remaining, () => {
        this._regenerateWall(bw.row, bw.col);
      });
      this._brokenWalls.set(`${bw.row},${bw.col}`, {
        timer,
        debrisSprite,
        brokenAt: Date.now() - (HAMMER_WALL_REGEN_TIME - bw.remaining),
      });
    }
  }

  /**
   * Clean up all timers, effects, and state.
   */
  destroy() {
    this._destroyed = true;

    // Cancel cadence timer
    if (this._cadenceTimer) {
      this._cadenceTimer.remove(false);
      this._cadenceTimer = null;
    }

    // Cancel all wall regen timers
    for (const [, data] of this._brokenWalls.entries()) {
      if (data.timer) data.timer.remove(false);
      if (data.debrisSprite) data.debrisSprite.destroy();
    }
    this._brokenWalls.clear();

    // Remove glow graphics
    if (this._strikeGlow) {
      this._strikeGlow.destroy();
      this._strikeGlow = null;
    }

    this.scene = null;
    this.eventBus = null;
    this.player = null;
  }

  // ---------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------

  /**
   * Get the currently held cursor direction.
   * @private
   * @returns {string|null} Direction enum value or null
   */
  _getHeldDirection() {
    if (!this.scene || !this.scene.cursors) return null;
    const c = this.scene.cursors;
    if (c.up.isDown) return Direction.UP;
    if (c.down.isDown) return Direction.DOWN;
    if (c.left.isDown) return Direction.LEFT;
    if (c.right.isDown) return Direction.RIGHT;
    return null;
  }

  /**
   * Reset the striking state.
   * @private
   */
  _resetStrike() {
    this._state = HammerState.IDLE;
    this._pressCount = 0;
    this._targetWall = null;
    if (this._cadenceTimer) {
      this._cadenceTimer.remove(false);
      this._cadenceTimer = null;
    }
    // Remove strike glow
    if (this._strikeGlow) {
      this._strikeGlow.clear();
    }
  }

  /**
   * Break the targeted wall: consume charge, update grid, visual effects,
   * schedule regeneration.
   * @private
   * @param {number} col
   * @param {number} row
   */
  _breakWall(col, row) {
    this._state = HammerState.BREAKING;

    // Consume 1 charge
    this._charges--;
    this.eventBus.emit(Events.HAMMER_CHARGES_CHANGED, this._charges);

    // Play break sound
    try {
      this.scene.sound.play(AUDIO_HAMMER_BREAK, { volume: getSfxVol(0.7) });
    } catch (_) { /* audio fallback */ }

    // Update maze grid: wall -> path
    this.scene.mazeGrid[row][col] = CellType.PATH;

    // Remove the wall sprite from the physics group
    this._removeWallSprite(col, row);

    // Celestial glow flash (2 tile radius)
    this._playBreakFlash(col, row);

    // Place debris texture on the ground
    const debrisSprite = this._placeDebris(col, row);

    // Emit wall broken event
    this.eventBus.emit(Events.HAMMER_WALL_BROKEN, { col, row });

    // Schedule wall regeneration
    const timer = this.scene.time.delayedCall(HAMMER_WALL_REGEN_TIME, () => {
      this._regenerateWall(row, col);
    });

    const key = `${row},${col}`;
    this._brokenWalls.set(key, {
      timer,
      debrisSprite,
      brokenAt: Date.now(),
    });

    // Reset strike state
    this.scene.time.delayedCall(300, () => {
      this._state = HammerState.IDLE;
      this._pressCount = 0;
      this._targetWall = null;
      if (this._strikeGlow) this._strikeGlow.clear();
    });
  }

  /**
   * Regenerate a broken wall back to its original state.
   * @private
   * @param {number} row
   * @param {number} col
   */
  _regenerateWall(row, col) {
    if (this._destroyed) return;

    const key = `${row},${col}`;
    const data = this._brokenWalls.get(key);

    // Don't regenerate if the player is standing on this cell
    if (this.player && !this.player.isMoving) {
      const pos = this.player.getGridPosition();
      if (pos.col === col && pos.row === row) {
        // Retry in 2 seconds
        const retryTimer = this.scene.time.delayedCall(2000, () => {
          this._regenerateWall(row, col);
        });
        if (data) data.timer = retryTimer;
        return;
      }
    }

    // Update maze grid: path -> wall
    this.scene.mazeGrid[row][col] = CellType.WALL;

    // Remove debris sprite
    if (data && data.debrisSprite) {
      data.debrisSprite.destroy();
    }

    // Create new wall sprite in the wall group
    if (this.scene.wallGroup) {
      const x = col * TILE_SIZE + TILE_SIZE / 2;
      const y = row * TILE_SIZE + TILE_SIZE / 2;
      const wallTexKey = this.scene._currentWallTexture;
      const wall = this.scene.wallGroup.create(x, y, wallTexKey);
      wall.setOrigin(0.5);
      wall.refreshBody();

      // Brief fade-in animation
      wall.setAlpha(0);
      this.scene.tweens.add({
        targets: wall,
        alpha: 1,
        duration: 600,
        ease: 'Sine.easeIn',
      });
    }

    // Play regen sound
    try {
      this.scene.sound.play(AUDIO_WALL_REGEN, { volume: getSfxVol(0.35) });
    } catch (_) { /* audio fallback */ }

    // Emit event
    this.eventBus.emit(Events.WALL_REGENERATED, { col, row });

    // Clean up tracking
    this._brokenWalls.delete(key);
  }

  /**
   * Find and remove the wall sprite at the given grid position.
   * @private
   * @param {number} col
   * @param {number} row
   */
  _removeWallSprite(col, row) {
    if (!this.scene.wallGroup) return;
    const x = col * TILE_SIZE + TILE_SIZE / 2;
    const y = row * TILE_SIZE + TILE_SIZE / 2;
    const children = this.scene.wallGroup.getChildren();
    for (let i = children.length - 1; i >= 0; i--) {
      const child = children[i];
      if (Math.abs(child.x - x) < 1 && Math.abs(child.y - y) < 1) {
        child.destroy();
        break;
      }
    }
  }

  /**
   * Place a debris/rubble sprite on the ground at the broken wall position.
   * @private
   * @param {number} col
   * @param {number} row
   * @returns {Phaser.GameObjects.Sprite}
   */
  _placeDebris(col, row) {
    const x = col * TILE_SIZE + TILE_SIZE / 2;
    const y = row * TILE_SIZE + TILE_SIZE / 2;
    const debris = this.scene.add.sprite(x, y, TEX_WALL_DEBRIS);
    debris.setOrigin(0.5);
    debris.setDepth(1); // Below entities, above floor
    debris.setAlpha(0.85);
    return debris;
  }

  /**
   * Subtle wall vibration on each B-press hit.
   * @private
   * @param {number} col
   * @param {number} row
   */
  _vibrateWall(col, row) {
    if (!this.scene.wallGroup) return;
    const x = col * TILE_SIZE + TILE_SIZE / 2;
    const y = row * TILE_SIZE + TILE_SIZE / 2;

    const children = this.scene.wallGroup.getChildren();
    for (const child of children) {
      if (Math.abs(child.x - x) < 1 && Math.abs(child.y - y) < 1) {
        const origX = child.x;
        const origY = child.y;
        this.scene.tweens.add({
          targets: child,
          x: origX + HAMMER_VIBRATE_INTENSITY,
          duration: 40,
          yoyo: true,
          repeat: 2,
          ease: 'Sine.easeInOut',
          onComplete: () => {
            child.x = origX;
            child.y = origY;
          },
        });
        break;
      }
    }
  }

  /**
   * Draw incremental glow around the targeted wall during striking.
   * @private
   * @param {number} col
   * @param {number} row
   * @param {number} progress - 0 to 1
   */
  _drawStrikeGlow(col, row, progress) {
    if (!this._strikeGlow) {
      this._strikeGlow = this.scene.add.graphics();
      this._strikeGlow.setDepth(200);
    }
    this._strikeGlow.clear();

    const x = col * TILE_SIZE + TILE_SIZE / 2;
    const y = row * TILE_SIZE + TILE_SIZE / 2;
    const radius = TILE_SIZE * 0.6 + progress * TILE_SIZE * 0.4;
    const alpha = 0.1 + progress * 0.35;

    this._strikeGlow.fillStyle(COLOR_HAMMER_BLUE, alpha);
    this._strikeGlow.fillCircle(x, y, radius);

    // Gold inner glow at higher progress
    if (progress > 0.5) {
      const goldAlpha = (progress - 0.5) * 0.6;
      this._strikeGlow.fillStyle(COLOR_HAMMER_GOLD, goldAlpha);
      this._strikeGlow.fillCircle(x, y, radius * 0.5);
    }
  }

  /**
   * Play the celestial glow flash when a wall is broken.
   * @private
   * @param {number} col
   * @param {number} row
   */
  _playBreakFlash(col, row) {
    const x = col * TILE_SIZE + TILE_SIZE / 2;
    const y = row * TILE_SIZE + TILE_SIZE / 2;
    const maxRadius = HAMMER_GLOW_RADIUS * TILE_SIZE;

    // Flash graphics
    const flash = this.scene.add.graphics();
    flash.setDepth(210);

    // Gold + blue expanding circle
    let flashAlpha = 0.8;
    flash.fillStyle(COLOR_HAMMER_GOLD, flashAlpha);
    flash.fillCircle(x, y, maxRadius * 0.3);
    flash.fillStyle(COLOR_HAMMER_BLUE, flashAlpha * 0.5);
    flash.fillCircle(x, y, maxRadius);

    // Animate flash fade
    this.scene.tweens.add({
      targets: { alpha: flashAlpha },
      alpha: 0,
      duration: HAMMER_GLOW_FLASH_DURATION,
      ease: 'Quad.easeOut',
      onUpdate: (tween) => {
        const a = tween.getValue();
        flash.clear();
        flash.fillStyle(COLOR_HAMMER_GOLD, a);
        flash.fillCircle(x, y, maxRadius * 0.3);
        flash.fillStyle(COLOR_HAMMER_BLUE, a * 0.5);
        flash.fillCircle(x, y, maxRadius);
      },
      onComplete: () => {
        flash.destroy();
      },
    });

    // Particle burst — divine debris sparkles
    const emitter = this.scene.add.particles(x, y, TEX_GLOW, {
      lifespan: 600,
      speed: { min: 30, max: 80 },
      angle: { min: 0, max: 360 },
      scale: { start: 0.5, end: 0 },
      alpha: { start: 0.8, end: 0 },
      frequency: -1,
    });
    emitter.setDepth(211);
    emitter.explode(8);
    this.scene.time.delayedCall(700, () => {
      if (emitter) emitter.destroy();
    });
  }

  /**
   * Check if a grid position is within maze bounds.
   * @private
   */
  _isWithinGrid(col, row) {
    return row >= 0 && row < this.scene.mazeGridHeight &&
           col >= 0 && col < this.scene.mazeGridWidth;
  }

  /**
   * Check whether a wall cell is on the outermost boundary of the maze.
   * Boundary walls form the perimeter and must never be destroyed.
   * @private
   * @param {number} col
   * @param {number} row
   * @returns {boolean}
   */
  _isBoundaryWall(col, row) {
    return row === 0 || col === 0 ||
           row === this.scene.mazeGridHeight - 1 ||
           col === this.scene.mazeGridWidth - 1;
  }

  /**
   * Direction to delta mapping.
   * @private
   * @param {string} dir
   * @returns {{ dx: number, dy: number }|null}
   */
  static _dirDelta(dir) {
    switch (dir) {
      case Direction.UP: return { dx: 0, dy: -1 };
      case Direction.DOWN: return { dx: 0, dy: 1 };
      case Direction.LEFT: return { dx: -1, dy: 0 };
      case Direction.RIGHT: return { dx: 1, dy: 0 };
      default: return null;
    }
  }
}
